import { sortByDate } from '@robovision/rvai-utils'
import * as idb from 'idb-keyval'
import {
  cloneDeep,
  isEqual,
} from 'lodash-es'
import { defineStore } from 'pinia'
import {
  markRaw,
  reactive,
  readonly,
} from 'vue'
import type { ImportExportController } from '~domain/importExport/ImportExportControllerOperation'
import type { ImportExportEventTriggerParams } from '~domain/importExport/ImportExportEventTriggerParams'
import type { ImportExportStep } from '~domain/importExport/ImportExportStep'
import type { ImportExportSubject } from '~domain/importExport/ImportExportSubject'


export enum ImportExportStoreType {
  Export = 'exports',
  Import = 'imports',
}

interface ImportExportStepState<T> {
  progress: number
  step: T
}

interface ImportExportBase {
  createdAt: Date
  eventTriggerParams: ImportExportEventTriggerParams
  parentId?: string
  rootDir: FileSystemDirectoryHandle
  subject: ImportExportSubject
  version: string
}

export interface ImportExportPayload<T extends ImportExportStep> extends ImportExportBase {
  controller: ImportExportController<T>
  id: string,
  stepGroups: T[][]
}

interface UnfinishedImportExport<T> extends ImportExportBase {
  stepGroups: ImportExportStepState<T>[][]
}
type PersistedState<T> = Record<string, UnfinishedImportExport<T>>

export interface ImportExportState<T extends ImportExportStep> extends ImportExportBase {
  controller?: ImportExportController<T>
  id: string
  paused: boolean
  stepGroups: ImportExportStepState<T>[][]
}

type State<T extends ImportExportStep> = Record<string, ImportExportState<T>>

export const buildImportExportStore = <T extends ImportExportStep>(
  storeType: ImportExportStoreType,
) => defineStore(storeType, () => {
    const state = reactive<State<T>>({})

    const actions = {
      addItem (payload: ImportExportPayload<T>) {
        const { id } = payload
        const itemState = {
          createdAt: payload.createdAt,
          eventTriggerParams: payload.eventTriggerParams,
          id,
          parentId: payload.parentId,
          rootDir: payload.rootDir,
          stepGroups: payload.stepGroups.map(group => group.map(step => ({
            progress: 0,
            step,
          }))),
          subject: payload.subject,
          version: payload.version,
        }

        state[id] = {
          controller: markRaw(payload.controller),
          paused: false,
          ...itemState,
        }

        idb.update<PersistedState<T>>(storeType, (items) => ({
          ...(items ?? {}),
          [id]: itemState,
        }))
      },

      async loadPersistedState () {
        const items = (await idb.get<PersistedState<T>>(storeType)) ?? {}

        for (const [id, itemState] of Object.entries(items)) {
          state[id] = {
            id,
            paused: true,
            ...itemState,
          }
        }
      },

      markPaused (id: string) {
        if (state[id]) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          state[id].paused = true
        }
      },

      markResumed (id: string, controller: ImportExportController<T>) {
        if (state[id]) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          state[id].controller = controller
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          state[id].paused = false
        }
      },

      async markStepCompleted (id: string, step: T) {
        if (!state[id]) return

        const { stepGroups = [] } = state[id] ?? {}
        for (const group of stepGroups) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          const stepState = group.find(state => isEqual(state.step, step))
          if (stepState) {
            stepState.progress = 1.0
            break
          }
        }

        try {
          await idb.update<PersistedState<T>>(storeType, (items) => {
            if (items?.[id]) {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              items[id].stepGroups = cloneDeep(stepGroups)
            }
            return items ?? {}
          })
        } catch (e) {
          console.error(e)
        }
      },

      removeItem (id: string) {
        delete state[id]

        idb.update<PersistedState<T>>(storeType, (items) => {
          if (items) delete items[id]
          return items ?? {}
        })
      },

      setState (params: Partial<typeof state>): void {
        Object.assign(state, params)
      },

      setStepProgress (id: string, step: T, progress: number) {
        if (!state[id]) return
        const { stepGroups = [] } = state[id] ?? {}
        for (const group of stepGroups) {
          const stepState = group.find(state => isEqual(state.step, step))
          if (stepState) {
            stepState.progress = progress
            break
          }
        }
      },

    }

    const getters = {
      getItems (subject: ImportExportSubject, parentId?: string): ImportExportState<T>[] {
        const items = Object.values(state) as ImportExportState<T>[]
        const filterFn: (item: ImportExportState<T>) => boolean = parentId
          ? item => item.subject === subject && item.parentId === parentId
          : item => item.subject === subject

        return sortByDate(items.filter(filterFn), 'createdAt', false)
      },

      getProgress (id: string): number {
        if (!state[id]) return 0

        const { stepGroups = [] } = state[id] ?? {}
        const totalSteps = stepGroups.flat().length
        if (!totalSteps) return 1 // possible case as all resources might present in system already
        return stepGroups.flat().map(step => step.progress ?? 0).reduce((sum, value) => sum + value, 0) / totalSteps
      },

      getUnfinishedStepGroups (id: string): T[][] {
        if (!state[id]) return []
        const { stepGroups = [] } = state[id] ?? {}
        return stepGroups
          .map(group => group
            .filter(({ progress }) => progress < 1.0)
            .map(({ step }) => step))
          .filter((group) => group.length) as T[][]
      },
    }

    return {
      state: readonly(state),
      ...actions,
      ...getters,
    }
  })
